import { useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ChartLineUp, Megaphone, Sparkle, CheckCircle, XCircle, Percent, ShareNetwork,
  ArrowClockwise, Storefront,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { PromoPageHeader } from "../components/PromoPageHeader";
import { KpiCard } from "../components/KpiCard";
import { EmptyState } from "../components/EmptyState";
import { TableSkeleton, KpiSkeleton } from "../components/Skeleton";
import { PromoSelect } from "../components/PromoSelect";
import { StatusBadge, StatusDot } from "../components/StatusBadge";
import { ProgressBar } from "../components/campaign/ProgressBar";
import { DataTable } from "../components/DataTable";
import { ConnectionHealth } from "../components/ConnectionHealth";
import { useQueueData, useConnectionHealth } from "../usePromo";
import { MARKETPLACES, SOCIAL_PLATFORMS, PLATFORM_BY_KEY } from "../platforms";
import { fmtDateTime, fmtRelative, fmtDuration } from "../campaignUtils";

const statusOf = (r) => {
  const s = r.post?.status || r.item.status;
  if (s === "pending" || s === "enqueued") return "scheduled";
  if (s === "processing" || s === "publishing") return "publishing";
  return s;
};
const errorOf = (r) => (r.post?.results || []).find((x) => x.ok === false)?.error || null;
const refDateOf = (r) => r.post?.published_at || r.item.planned_publish_at || r.post?.scheduled_at || null;
const DAY = 86400e3;

function rate(published, failed) {
  const total = published + failed;
  return total ? Math.round((published / total) * 100) : null;
}

export default function PerformanceAnalytics() {
  const { t } = useTranslation();
  const { rows, events, promotions, loading, reload } = useQueueData();
  const conn = useConnectionHealth();

  const [range, setRange] = useState("all");
  const [marketplace, setMarketplace] = useState("all");
  const [campaign, setCampaign] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [provider, setProvider] = useState("all");

  const enriched = useMemo(
    () => rows.map((r) => ({
      ...r,
      id: r.item.id,
      status: statusOf(r),
      error: errorOf(r),
      marketplace: r.promo.source_platform || null,
      platform: r.item.platform || null,
      provider: r.promo.ai?.provider || null,
      ref: refDateOf(r),
    })),
    [rows],
  );

  // Distinct filter option values (from real data only).
  const marketplaces = useMemo(() => [...new Set(enriched.map((r) => r.marketplace).filter(Boolean))], [enriched]);
  const platforms = useMemo(() => [...new Set(enriched.map((r) => r.platform).filter(Boolean))], [enriched]);
  const providers = useMemo(() => [...new Set(enriched.map((r) => r.provider).filter(Boolean))], [enriched]);
  const campaignOpts = useMemo(() => {
    const seen = {}; enriched.forEach((r) => { seen[r.promo.id] = r.promo.name; });
    return Object.entries(seen).map(([id, name]) => ({ id, name }));
  }, [enriched]);

  const filtered = useMemo(() => {
    const since = range === "all" ? null : Date.now() - Number(range) * DAY;
    return enriched.filter((r) => {
      if (marketplace !== "all" && r.marketplace !== marketplace) return false;
      if (campaign !== "all" && r.promo.id !== campaign) return false;
      if (platform !== "all" && r.platform !== platform) return false;
      if (provider !== "all" && r.provider !== provider) return false;
      if (since && r.ref) return new Date(r.ref).getTime() >= since;
      if (since && !r.ref) return false;
      return true;
    });
  }, [enriched, range, marketplace, campaign, platform, provider]);

  // KPIs (real data).
  const kpi = useMemo(() => {
    const published = filtered.filter((r) => r.status === "published").length;
    const failed = filtered.filter((r) => r.status === "failed").length;
    const activeCampaigns = promotions.filter((p) => p.status === "active").length;
    const mkConn = MARKETPLACES.filter((mk) => conn.sources.some((s) => s.connector_id === mk.key && s.status === "connected")).length;
    const scConn = new Set(conn.accounts.filter((a) => a.status === "connected").map((a) => a.platform)).size;
    return { published, failed, activeCampaigns, successRate: rate(published, failed), connected: mkConn + scConn, mkConn, scConn };
  }, [filtered, promotions, conn.sources, conn.accounts]);

  // Section 1 — per-campaign aggregate.
  const campaignPerf = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      const g = map.get(r.promo.id) || { promo: r.promo, total: 0, published: 0, failed: 0, dates: [] };
      g.total += 1;
      if (r.status === "published") g.published += 1;
      if (r.status === "failed") g.failed += 1;
      if (r.ref) g.dates.push(new Date(r.ref).getTime());
      map.set(r.promo.id, g);
    });
    return [...map.values()].map((g) => {
      const span = g.dates.length ? Math.max(...g.dates) - Math.min(...g.dates) : null;
      return { ...g, pct: g.total ? Math.round((g.published / g.total) * 100) : 0, durationMs: span };
    }).sort((a, b) => b.total - a.total);
  }, [filtered]);

  // Section 2 / 3 — marketplace & platform breakdown.
  const groupBy = useCallback((key) => {
    const map = new Map();
    filtered.forEach((r) => {
      const k = r[key];
      if (!k) return;
      const g = map.get(k) || { key: k, total: 0, published: 0, failed: 0 };
      g.total += 1;
      if (r.status === "published") g.published += 1;
      if (r.status === "failed") g.failed += 1;
      map.set(k, g);
    });
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [filtered]);
  const marketplacePerf = useMemo(() => groupBy("marketplace"), [groupBy]);
  const platformPerf = useMemo(() => groupBy("platform"), [groupBy]);

  // Section 4 — publishing activity buckets (published_at by day).
  const activity = useMemo(() => {
    const days = range === "all" ? 30 : Number(range);
    const buckets = [];
    const start = new Date(); start.setHours(0, 0, 0, 0);
    for (let i = days - 1; i >= 0; i -= 1) {
      const d = new Date(start.getTime() - i * DAY);
      buckets.push({ ts: d.getTime(), label: d.toLocaleDateString(undefined, { day: "2-digit", month: "short" }), published: 0, failed: 0 });
    }
    const byTs = new Map(buckets.map((b) => [b.ts, b]));
    filtered.forEach((r) => {
      const at = r.post?.published_at || (r.status === "failed" ? r.ref : null);
      if (!at) return;
      const d = new Date(at); d.setHours(0, 0, 0, 0);
      const b = byTs.get(d.getTime());
      if (!b) return;
      if (r.status === "published") b.published += 1;
      else if (r.status === "failed") b.failed += 1;
    });
    const max = Math.max(1, ...buckets.map((b) => b.published + b.failed));
    return { buckets, max, hasData: buckets.some((b) => b.published + b.failed > 0) };
  }, [filtered, range]);

  // Section 5 — errors.
  const errors = useMemo(
    () => filtered.filter((r) => r.status === "failed" || r.error)
      .sort((a, b) => new Date(b.ref || 0) - new Date(a.ref || 0)),
    [filtered],
  );

  const opt = (vals, allLabel) => [{ value: "all", label: allLabel }, ...vals];
  const mktLabel = (k) => MARKETPLACES.find((m) => m.key === k)?.label || k;

  return (
    <div className="space-y-8" data-testid="performance-page">
      <PromoPageHeader
        overline={t("promo.nav.performance")}
        title={t("promo.perf.title")}
        description={t("promo.perf.subtitle")}
        actions={
          <button onClick={reload} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" data-testid="perf-refresh">
            <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> {t("promo.conn.refresh")}
          </button>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="perf-kpis">
        <KpiCard index={0} testId="perf-kpi-campaigns" label={t("promo.perf.kpi.campaigns")} value={promotions.length} Icon={Megaphone} />
        <KpiCard index={1} testId="perf-kpi-active" label={t("promo.perf.kpi.active")} value={kpi.activeCampaigns} Icon={Sparkle} />
        <KpiCard index={2} testId="perf-kpi-published" label={t("promo.perf.kpi.published")} value={kpi.published} Icon={CheckCircle} />
        <KpiCard index={3} testId="perf-kpi-failed" label={t("promo.perf.kpi.failed")} value={kpi.failed} Icon={XCircle} />
        <KpiCard index={4} testId="perf-kpi-success" label={t("promo.perf.kpi.success")} value={kpi.successRate == null ? "—" : `${kpi.successRate}%`} Icon={Percent} />
        <KpiCard index={5} testId="perf-kpi-connected" label={t("promo.perf.kpi.connected")} value={kpi.connected} Icon={ShareNetwork} hint={t("promo.dash.kpi.connHint", { mk: kpi.mkConn, sc: kpi.scConn })} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2" data-testid="perf-toolbar">
        <div className="w-40"><PromoSelect value={range} onChange={setRange} testId="perf-range"
          options={[
            { value: "all", label: t("promo.perf.range.all") },
            { value: "7", label: t("promo.perf.range.d7") },
            { value: "30", label: t("promo.perf.range.d30") },
            { value: "90", label: t("promo.perf.range.d90") },
          ]} /></div>
        <div className="w-40"><PromoSelect value={marketplace} onChange={setMarketplace} testId="perf-marketplace"
          options={opt(marketplaces.map((mk) => ({ value: mk, label: mktLabel(mk) })), t("promo.catalog.allMarketplaces"))} /></div>
        <div className="w-44"><PromoSelect value={campaign} onChange={setCampaign} testId="perf-campaign"
          options={opt(campaignOpts.map((c) => ({ value: c.id, label: c.name })), t("promo.queue.allCampaigns"))} /></div>
        <div className="w-36"><PromoSelect value={platform} onChange={setPlatform} testId="perf-platform"
          options={opt(platforms.map((p) => ({ value: p, label: PLATFORM_BY_KEY[p]?.label || p })), t("promo.queue.allPlatforms"))} /></div>
        <div className="w-40"><PromoSelect value={provider} onChange={setProvider} testId="perf-provider"
          options={opt(providers.map((p) => ({ value: p, label: p })), t("promo.perf.allProviders"))} /></div>
      </div>

      {loading ? (
        <div className="space-y-8"><TableSkeleton rows={4} cols={6} /><KpiSkeleton count={4} className="lg:grid-cols-2" /></div>
      ) : enriched.length === 0 ? (
        <EmptyState Icon={ChartLineUp} title={t("promo.perf.emptyTitle")} description={t("promo.perf.emptyDesc")}
          action={<Link to="/promotions/new" className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground" data-testid="perf-empty-cta">{t("promo.nav.new")}</Link>} />
      ) : (
        <div className="space-y-10">
          {/* Section 1 — Campaign Performance */}
          <Section title={t("promo.perf.sec.campaigns")} testId="perf-campaigns">
            {campaignPerf.length === 0 ? <NoData t={t} /> : (
              <DataTable
                testId="perf-campaign-table"
                rowKey="id"
                rows={campaignPerf.map((g) => ({ id: g.promo.id, ...g }))}
                columns={[
                  { key: "name", label: t("promo.perf.col.campaign"), render: (g) => (
                    <Link to={`/promotions/campaigns/${g.promo.id}`} className="flex items-center gap-2 font-medium hover:text-brand">
                      <StatusDot status={g.promo.status === "active" ? "active" : g.promo.status} /> {g.promo.name}
                    </Link>
                  ) },
                  { key: "status", label: t("promo.perf.col.status"), render: (g) => <StatusBadge status={g.promo.status} label={t(`promo.campaigns.status.${g.promo.status}`, g.promo.status)} /> },
                  { key: "published", label: t("promo.perf.col.published"), align: "right", render: (g) => <span className="tabular-nums text-success">{g.published}</span> },
                  { key: "failed", label: t("promo.perf.col.errors"), align: "right", render: (g) => <span className={cn("tabular-nums", g.failed ? "text-destructive" : "text-muted-foreground")}>{g.failed}</span> },
                  { key: "progress", label: t("promo.perf.col.progress"), render: (g) => (
                    <div className="flex items-center gap-2">
                      <ProgressBar value={g.pct} tone={g.pct === 100 ? "success" : "brand"} className="w-24" />
                      <span className="tabular-nums text-xs text-muted-foreground">{g.pct}%</span>
                    </div>
                  ) },
                  { key: "duration", label: t("promo.perf.col.duration"), align: "right", render: (g) => <span className="tabular-nums text-muted-foreground">{fmtDuration(g.durationMs)}</span> },
                ]}
              />
            )}
          </Section>

          {/* Section 2 & 3 — Marketplace + Platform Overview */}
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <Section title={t("promo.perf.sec.marketplaces")} testId="perf-marketplaces">
              <BreakdownGrid items={marketplacePerf} lookup={(k) => MARKETPLACES.find((m) => m.key === k)} t={t} testPrefix="perf-mk" />
            </Section>
            <Section title={t("promo.perf.sec.platforms")} testId="perf-platforms">
              <BreakdownGrid items={platformPerf} lookup={(k) => SOCIAL_PLATFORMS.find((p) => p.key === k)} t={t} testPrefix="perf-pl" />
            </Section>
          </div>

          {/* Section 4 — Publishing Activity */}
          <Section title={t("promo.perf.sec.activity")} testId="perf-activity">
            {!activity.hasData ? <NoData t={t} /> : (
              <div className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-end gap-1.5 overflow-x-auto pb-2" style={{ height: 180 }} data-testid="perf-activity-chart">
                  {activity.buckets.map((b) => {
                    const total = b.published + b.failed;
                    const h = Math.round((total / activity.max) * 150);
                    return (
                      <div key={b.ts} className="group flex min-w-[10px] flex-1 flex-col items-center justify-end gap-1" title={`${b.label} · ${b.published} ✓ / ${b.failed} ✕`}>
                        <div className="flex w-full flex-col-reverse items-stretch" style={{ height: h }}>
                          {b.published > 0 && <div className="w-full rounded-t-sm bg-success/70 transition-colors group-hover:bg-success" style={{ height: `${(b.published / total) * 100}%` }} />}
                          {b.failed > 0 && <div className="w-full bg-destructive/70 transition-colors group-hover:bg-destructive" style={{ height: `${(b.failed / total) * 100}%` }} />}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-success" /> {t("promo.perf.col.published")}</span>
                  <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-destructive" /> {t("promo.perf.col.errors")}</span>
                </div>
              </div>
            )}
          </Section>

          {/* Section 5 — Error Analysis */}
          <Section title={t("promo.perf.sec.errors")} testId="perf-errors">
            {errors.length === 0 ? (
              <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-6 text-sm text-success">
                <CheckCircle size={18} weight="fill" /> {t("promo.perf.noErrors")}
              </div>
            ) : (
              <DataTable
                testId="perf-errors-table"
                rowKey="id"
                rows={errors}
                columns={[
                  { key: "when", label: t("promo.timeline.f.timestamp"), render: (r) => <span className="text-muted-foreground">{fmtRelative(r.ref)}</span> },
                  { key: "campaign", label: t("promo.perf.col.campaign"), render: (r) => (
                    <Link to={`/promotions/campaigns/${r.promo.id}`} className="hover:text-brand">{r.promo.name}</Link>
                  ) },
                  { key: "platform", label: t("promo.perf.col.platform"), render: (r) => {
                    const p = PLATFORM_BY_KEY[r.platform];
                    return <span className="flex items-center gap-1.5">{p && <p.Icon size={13} />}{p?.label || r.platform}</span>;
                  } },
                  { key: "error", label: t("promo.perf.col.error"), render: (r) => (
                    <span className="block max-w-[280px] truncate text-destructive" title={r.error || ""}>{r.error || t("promo.perf.unknownError")}</span>
                  ) },
                ]}
              />
            )}
          </Section>

          {/* Section 6 — Connection Health */}
          <section data-testid="perf-connection-health">
            <ConnectionHealth title={t("promo.conn.title")} />
          </section>

          {/* Section 7 — Recent Activity */}
          <Section title={t("promo.dash.recentActivity")} testId="perf-recent">
            <div className="rounded-lg border border-border bg-card p-5">
              {events.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("promo.detail.noEvents")}</p>
              ) : (
                <div className="relative space-y-3 border-l border-border pl-5">
                  {events.slice(0, 10).map((e) => (
                    <div key={e.id} className="relative" data-testid={`perf-activity-${e.id}`}>
                      <span className="absolute -left-[23px] top-1 h-1.5 w-1.5 rounded-full bg-brand" />
                      <div className="flex items-center justify-between gap-3">
                        <span className="truncate font-mono text-xs">{e.type}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">{fmtRelative(e.created_at)}</span>
                      </div>
                      {e.actor_email && <p className="truncate text-[11px] text-muted-foreground">{e.actor_email}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Section>
        </div>
      )}
    </div>
  );
}

function Section({ title, testId, children }) {
  return (
    <section className="space-y-4" data-testid={testId}>
      <h3 className="text-lg font-medium tracking-tight">{title}</h3>
      {children}
    </section>
  );
}

function NoData({ t }) {
  return <div className="rounded-lg border border-border bg-card px-4 py-6 text-sm text-muted-foreground">{t("promo.perf.noData")}</div>;
}

function BreakdownGrid({ items, lookup, t, testPrefix }) {
  if (!items.length) return <NoData t={t} />;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid={`${testPrefix}-grid`}>
      {items.map((g, i) => {
        const meta = lookup(g.key);
        const r = rate(g.published, g.failed);
        return (
          <motion.div key={g.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.04 }}
            className="rounded-lg border border-border bg-card p-4" data-testid={`${testPrefix}-${g.key}`}>
            <div className="flex items-center gap-2">
              {meta?.Icon ? <meta.Icon size={16} /> : <Storefront size={16} className="text-muted-foreground" />}
              <span className="text-sm font-medium">{meta?.label || g.key}</span>
              <span className="ml-auto text-xs tabular-nums text-muted-foreground">{g.total}</span>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <ProgressBar value={r ?? 0} tone={r === 100 ? "success" : "brand"} className="flex-1" />
              <span className="tabular-nums text-xs font-medium">{r == null ? "—" : `${r}%`}</span>
            </div>
            <div className="mt-2 flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1 text-success"><CheckCircle size={12} weight="fill" /> {g.published}</span>
              <span className={cn("flex items-center gap-1", g.failed ? "text-destructive" : "text-muted-foreground")}><XCircle size={12} weight="fill" /> {g.failed}</span>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
